import React, { createContext, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { CourseIDContext } from './CourseIDContext';
import { AuthContext } from './AuthContext';

const ArchiveExportContext = createContext();

const ArchiveExportProvider = ({ children }) => {
  const { courseID } = useContext(CourseIDContext);
  const { isInstructor, isTA } = useContext(AuthContext);
  const [archiveStatus, setArchiveStatus] = useState({
    isArchiving: false,
    isExporting: false,
    lastArchiveDate: null,
    lastExportDate: null,
    message: '',
    error: null
  });

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Check if course version already exists
  const checkCourseVersion = useCallback(async (courseVersion) => {
    try {
      const response = await axios.get(`${apiUrl}/api/archive/check-version/${courseID}/${courseVersion}`);
      return response.data;
    } catch (error) {
      console.error('Error checking course version:', error);
      throw error;
    }
  }, [courseID, apiUrl]);

  // Archive all course data with courseVersion
  const archiveCourseData = useCallback(async (courseVersion, overwrite = false) => {
    if (!isInstructor && !isTA) {
      setArchiveStatus(prev => ({ 
        ...prev, 
        error: 'Unauthorized: Only instructors and TAs can archive data' 
      }));
      return;
    }

    setArchiveStatus(prev => ({ 
      ...prev, 
      isArchiving: true, 
      error: null, 
      message: `Archiving course version '${courseVersion}'...` 
    }));

    try {
      const response = await axios.post(`${apiUrl}/api/archive/course-data/${courseID}`, {
        courseVersion,
        overwrite
      });

      setArchiveStatus(prev => ({
        ...prev,
        isArchiving: false,
        lastArchiveDate: new Date().toISOString(),
        message: response.data.message || `Course version '${courseVersion}' archived successfully`
      }));

      return response.data;
    } catch (error) {
      console.error('Error archiving course data:', error);
      setArchiveStatus(prev => ({
        ...prev,
        isArchiving: false,
        error: error.response?.data?.message || 'Failed to archive course data'
      }));
      throw error;
    }
  }, [courseID, isInstructor, isTA, apiUrl]);

  // Export data to various formats
  const exportData = useCallback(async (dataType, format = 'csv', filters = {}) => {
    if (!isInstructor && !isTA) {
      setArchiveStatus(prev => ({ 
        ...prev, 
        error: 'Unauthorized: Only instructors and TAs can export data' 
      }));
      return;
    }

    setArchiveStatus(prev => ({ 
      ...prev, 
      isExporting: true, 
      error: null, 
      message: `Exporting ${dataType} as ${format.toUpperCase()}...` 
    }));

    try {
      const response = await axios.post(`${apiUrl}/api/export/data/${courseID}`, {
        dataType,
        format,
        filters
      }, {
        responseType: 'blob' // Important for file downloads
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_${courseID}_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setArchiveStatus(prev => ({
        ...prev,
        isExporting: false,
        lastExportDate: new Date().toISOString(),
        message: `${dataType} exported successfully as ${format.toUpperCase()}`
      }));

      return true;
    } catch (error) {
      console.error('Error exporting data:', error);
      setArchiveStatus(prev => ({
        ...prev,
        isExporting: false,
        error: error.response?.data?.message || `Failed to export ${dataType}`
      }));
      throw error;
    }
  }, [courseID, isInstructor, isTA, apiUrl]);

  // Export all archived data
  const exportAllArchivedData = useCallback(async (format = 'xlsx') => {
    if (!isInstructor && !isTA) {
      setArchiveStatus(prev => ({ 
        ...prev, 
        error: 'Unauthorized: Only instructors and TAs can export data' 
      }));
      return;
    }

    setArchiveStatus(prev => ({ 
      ...prev, 
      isExporting: true, 
      error: null, 
      message: `Exporting all archived data as ${format.toUpperCase()}...` 
    }));

    try {
      const response = await axios.post(`${apiUrl}/api/export/archived-data/${courseID}`, {
        format
      }, {
        responseType: 'blob' // Important for file downloads
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `all_archived_data_${courseID}_${new Date().toISOString().split('T')[0]}.${format}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setArchiveStatus(prev => ({
        ...prev,
        isExporting: false,
        message: `All archived data exported successfully as ${filename}`
      }));

      return true;
    } catch (error) {
      console.error('Error exporting all archived data:', error);
      setArchiveStatus(prev => ({
        ...prev,
        isExporting: false,
        error: error.response?.data?.message || 'Failed to export all archived data'
      }));
      throw error;
    }
  }, [courseID, isInstructor, isTA, apiUrl]);

  // Check if any archive tables exist
  const checkArchiveTablesExist = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/export/check-archived-tables/${courseID}`);
      return response.data.hasArchives || false;
    } catch (error) {
      console.error('Error checking archive tables:', error);
      return false;
    }
  }, [courseID, apiUrl]);

  // Get archive history
  const getArchiveHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/archive/history/${courseID}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching archive history:', error);
      setArchiveStatus(prev => ({
        ...prev,
        error: 'Failed to fetch archive history'
      }));
      // Return empty array instead of throwing error
      return [];
    }
  }, [courseID, apiUrl]);

  // Get archived course versions
  const getArchivedVersions = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/archive/versions/${courseID}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching archived versions:', error);
      throw error;
    }
  }, [courseID, apiUrl]);

  // Clear status messages
  const clearStatus = useCallback(() => {
    setArchiveStatus(prev => ({
      ...prev,
      message: '',
      error: null
    }));
  }, []);

  // Clear student data
  const clearStudentData = useCallback(async () => {
    setArchiveStatus(prev => ({
      ...prev,
      isClearing: true,
      message: '',
      error: null
    }));

    try {
      const response = await axios.post(`${apiUrl}/api/archive/clear-student-data/${courseID}`);
      
      setArchiveStatus(prev => ({
        ...prev,
        isClearing: false,
        message: response.data.message
      }));

      return response.data;
    } catch (error) {
      console.error('Error clearing student data:', error);
      setArchiveStatus(prev => ({
        ...prev,
        isClearing: false,
        error: error.response?.data?.message || 'Failed to clear student data'
      }));
      throw error;
    }
  }, [courseID, apiUrl]);

  // Delete archive
  const deleteArchive = useCallback(async (courseVersion) => {
    setArchiveStatus(prev => ({
      ...prev,
      isDeleting: true,
      message: '',
      error: null
    }));

    try {
      const response = await axios.delete(`${apiUrl}/api/archive/delete/${courseID}/${courseVersion}`);
      
      setArchiveStatus(prev => ({
        ...prev,
        isDeleting: false,
        message: response.data.message
      }));

      return response.data;
    } catch (error) {
      console.error('Error deleting archive:', error);
      setArchiveStatus(prev => ({
        ...prev,
        isDeleting: false,
        error: error.response?.data?.message || 'Failed to delete archive'
      }));
      throw error;
    }
  }, [courseID, apiUrl]);

  const value = {
    archiveStatus,
    checkCourseVersion,
    archiveCourseData,
    exportData,
    exportAllArchivedData,
    checkArchiveTablesExist,
    getArchiveHistory,
    getArchivedVersions,
    clearStatus,
    clearStudentData,
    deleteArchive
  };

  return (
    <ArchiveExportContext.Provider value={value}>
      {children}
    </ArchiveExportContext.Provider>
  );
};

export { ArchiveExportContext, ArchiveExportProvider };
